import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { When } from 'react-if';
import { LoadingButton } from '@mui/lab';

import { Dropzone } from '../dropzone';
import { DeviceTypes } from '../../types';
import { useGenericMutation } from '../../services/react-query/generic.mutation';
import { handleApiError } from '../../services/react-query/react-query.client';
import { useGenericQuery } from '../../services/react-query/generic.query';
import Loading from '../loading';

interface BulkCreateResult {
    created: number;
    updated: number;
    unchanged: number;
    duplicateAssetTags: string[];
}

// One parsed CSV row. The shipped* columns are the shipping-manifest
// snapshot (who the device was shipped to, and their group/team at ship
// time) — optional, free text, import is their only writer.
type ImportDeviceRow = Pick<
    DeviceTypes.Model,
    'serialNumber' | 'assetTag' | 'shippedTo' | 'shippedToEmployeeId' | 'shippedToGroup' | 'shippedToTeam'
>;

interface Props {
    visible: boolean;
    onClose: () => void;
}

export const ImportDevicesModal: React.FC<Props> = ({
    visible,
    onClose,
}) => {
    const [selectedFile, setSelectedFile] = useState<File>();
    const [file64, setFile64] = useState<string | null>(null);
    const [devicesToImport, setDevicesToImport] = useState<ImportDeviceRow[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<BulkCreateResult | null>(null);
    const [skippedRows, setSkippedRows] = useState<number>(0);

    const { data: remainingDevicesToCreate, isLoading: isLoadingCanCreate } = useGenericQuery<number>(
        'customerCanCreateDevices',
        'devices/getRemainingCountDevicesToCreate',
        undefined,
        undefined,
        false,
    );

    const {
        mutateAsync,
        isLoading,
    } = useGenericMutation<{ devices: ImportDeviceRow[] }, BulkCreateResult>('importDevices', `devices/bulkCreate`, 'post');

    const parseCSV = (file: File) => {
        Papa.parse(file, {
            header: true,
            // 'greedy' drops lines that are empty or contain only whitespace/commas
            // (e.g. the trailing ",,,," rows Excel/Sheets append on export), so they
            // never become phantom blank-serial devices.
            skipEmptyLines: 'greedy',
            complete: (result: any) => {
                const parsedRows = (result.data as ImportDeviceRow[])
                    .map((row) => ({ ...row, serialNumber: row.serialNumber?.trim() ?? '' }));
                // Backstop: drop any remaining row without a usable serial number.
                // A device with no serial is meaningless, and the API now rejects
                // the whole batch if one slips through.
                const data = parsedRows.filter((row) => row.serialNumber !== '');

                setDevicesToImport(data);
                // Surface how many content rows were dropped so the imported count
                // doesn't silently mismatch the user's spreadsheet row count.
                setSkippedRows(parsedRows.length - data.length);
            },
            error: (error: any) => {
                toast(
                    'An error ocurred while trying to import the file, the format is invalid.',
                    {
                        autoClose: 1500,
                        type: 'error',
                    },
                );
            },
        });
    };

    const handleClose = () => {
        setSummary(null);
        setError(null);
        setFile64(null);
        setSelectedFile(undefined);
        setDevicesToImport([]);
        setSkippedRows(0);
        onClose();
    };

    const onSubmit = async () => {
        await mutateAsync({ devices: devicesToImport }, ({
            onError: (err) => {
                const msg = handleApiError(err);
                setError(msg);
            },
            onSuccess: (data: any) => {
                const result = data?.result as BulkCreateResult;
                if (result?.duplicateAssetTags?.length > 0) {
                    setError(`Duplicate asset tags found: ${result.duplicateAssetTags.join(', ')}. Each asset tag must be unique.`);
                    return;
                }
                setFile64(null);
                setSelectedFile(undefined);
                setDevicesToImport([]);
                setSkippedRows(0);
                setSummary(result);
            },
        }));
    };

    return (
        <Dialog
            scroll={'paper'}
            onClose={handleClose}
            open={visible}
        >
            <DialogTitle fontWeight={500} mb={1} id="alert-dialog-title">
                Import Devices
            </DialogTitle>
            <DialogContent>
                {summary ? (
                    <>
                        <Alert variant='standard' color='success' sx={{ mb: 2 }}>
                            Import completed successfully!
                        </Alert>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{summary.created}</strong> new device{summary.created !== 1 ? 's' : ''} added
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{summary.updated}</strong> existing device{summary.updated !== 1 ? 's' : ''} updated (asset tag / shipping info)
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>{summary.unchanged}</strong> device{summary.unchanged !== 1 ? 's' : ''} unchanged
                        </Typography>
                    </>
                ) : (
                    <>
                        <DialogContentText id="alert-dialog-description">
                            It's possible to import a list of devices by uploading a .csv file.
                        </DialogContentText>

                        <When condition={isLoadingCanCreate}>
                            <Loading
                                loading='Checking if your subscription allows you to create more devices...'
                            />
                        </When>

                        <When condition={!isLoadingCanCreate && remainingDevicesToCreate}>
                            <Grid display={'flex'} alignItems={'center'} justifyItems={'center'} alignSelf={'center'} flexDirection={'row'} spacing={3} mb={3}>
                                <Button
                                    size='small'
                                    variant='text'
                                    sx={{ mt: 2 }}
                                    onClick={() => {
                                        const csv = Papa.unparse([{
                                            serialNumber: 'S240200630',
                                            assetTag: '',
                                            shippedTo: '',
                                            shippedToEmployeeId: '',
                                            shippedToGroup: '',
                                            shippedToTeam: '',
                                        }]);

                                        const element = document.createElement("a");
                                        const file = new Blob([csv], { type: 'text/csv' });
                                        element.href = URL.createObjectURL(file);
                                        element.download = "pom_devices_template.csv";
                                        document.body.appendChild(element);
                                        element.click();
                                    }}
                                >
                                    Download csv template
                                </Button>
                            </Grid>
                            <Dropzone
                                file={file64 || undefined}
                                selectedFile={selectedFile}
                                title={`You can import ${remainingDevicesToCreate} devices left to your account`}
                                onChange={(b64, file) => {
                                    if (file.type !== "text/csv") {
                                        toast(
                                            'Invalid file type, should be a .csv file.',
                                            {
                                                autoClose: 1500,
                                                type: 'error',
                                            }
                                        );
                                        return;
                                    }
                                    setSelectedFile(file);
                                    setFile64(b64);
                                    parseCSV(file);
                                }}
                            />
                        </When>

                        {skippedRows > 0 &&
                            <Alert variant='standard' color='warning' sx={{ mb: 2, mt: 2 }}>
                                Loaded {devicesToImport.length} of {devicesToImport.length + skippedRows} rows
                                {' — '}{skippedRows} row{skippedRows !== 1 ? 's' : ''} skipped (blank serial number).
                            </Alert>
                        }

                        {error &&
                            <Alert variant='standard' color='error' sx={{ mb: 2, mt: 2 }}>
                                {error}
                            </Alert>
                        }
                    </>
                )}
            </DialogContent>
            <When condition={!isLoadingCanCreate}>
                <DialogActions>
                    {summary ? (
                        <Button onClick={handleClose}>Done</Button>
                    ) : (
                        <>
                            <Button disabled={isLoadingCanCreate} color='inherit' onClick={handleClose}>
                                Cancel
                            </Button>
                            <LoadingButton onClick={onSubmit} loading={isLoading} disabled={devicesToImport?.length === 0} autoFocus>Confirm</LoadingButton>
                        </>
                    )}
                </DialogActions>
            </When>
        </Dialog>
    );
};
